import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

const navLinks = [
  { label: 'Soluções', href: '#solucoes' },
  { label: 'Segmentos', href: '#segmentos' },
  { label: 'Diferenciais', href: '#diferenciais' },
  { label: 'Sistemas', href: '#sistemas' },
  { label: 'Contato', href: '#contato' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrolledBg = isDark
    ? 'bg-[#0a0f1e]/95 backdrop-blur-md shadow-lg shadow-black/30'
    : 'bg-white/95 backdrop-blur-md shadow-lg shadow-slate-200/60';

  const navTextColor = isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900';
  const mobileMenuBg = isDark
    ? 'bg-[#0a0f1e]/98 backdrop-blur-md border-t border-white/10'
    : 'bg-white/98 backdrop-blur-md border-t border-slate-200';

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? scrolledBg : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <a href="#" className="flex items-center gap-3 group cursor-pointer">
          <div className="w-10 h-10 flex items-center justify-center overflow-hidden rounded-xl">
            <img
              src="https://storage.readdy-site.link/project_files/4a2f3690-6afc-4752-8f11-296a8cd10aa8/9df4885e-fe7a-4292-927f-532659452029_Gemini_Generated_Image_e0eqt8e0eqt8e0eq.png?v=c10a7e5f8467af600c66ebccd92d7add"
              alt="Granato Tecnologia"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex flex-col leading-none">
            <span className={`font-bold text-lg tracking-tight ${isDark ? 'text-white' : scrolled ? 'text-slate-900' : 'text-slate-900'}`}>
              Granato
            </span>
            <span className="text-cyan-400 text-xs font-medium tracking-widest uppercase">Tecnologia</span>
          </div>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNav(link.href)}
              className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                isDark
                  ? 'text-slate-300 hover:text-white'
                  : scrolled
                  ? 'text-slate-600 hover:text-slate-900'
                  : 'text-slate-700 hover:text-cyan-600'
              }`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* CTA + Theme Toggle */}
        <div className="hidden md:flex items-center gap-3">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
              isDark
                ? 'border-white/15 bg-white/5 text-slate-300 hover:text-white hover:bg-white/10'
                : scrolled
                ? 'border-slate-200 bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                : 'border-slate-300 bg-white/60 text-slate-600 hover:text-cyan-600 hover:bg-white/80'
            }`}
            title={isDark ? 'Modo Claro' : 'Modo Escuro'}
          >
            <i className={`text-base ${isDark ? 'ri-sun-line' : 'ri-moon-line'}`}></i>
          </button>

          <a
            href="#contato"
            onClick={(e) => { e.preventDefault(); handleNav('#contato'); }}
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              isDark
                ? 'text-slate-300 hover:text-white'
                : scrolled
                ? 'text-slate-600 hover:text-slate-900'
                : 'text-slate-700 hover:text-cyan-600'
            }`}
          >
            Fale Conosco
          </a>
          <Link
            to="/login"
            className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer overflow-hidden group"
            style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}
          >
            <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
            <i className="ri-dashboard-3-line text-white text-base"></i>
            <span className="text-white">Gestão de Projetos</span>
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
          </Link>
        </div>

        {/* Mobile: Theme + Hamburger */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
              isDark
                ? 'border-white/15 bg-white/5 text-slate-300'
                : 'border-slate-300 bg-white/60 text-slate-600'
            }`}
          >
            <i className={`text-base ${isDark ? 'ri-sun-line' : 'ri-moon-line'}`}></i>
          </button>
          <button
            className={`w-10 h-10 flex items-center justify-center cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className={`md:hidden px-6 py-6 flex flex-col gap-4 ${mobileMenuBg}`}>
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNav(link.href)}
              className={`text-base font-medium text-left transition-colors cursor-pointer whitespace-nowrap ${navTextColor}`}
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/login"
            className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold text-white whitespace-nowrap cursor-pointer"
            style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}
            onClick={() => setMenuOpen(false)}
          >
            <i className="ri-dashboard-3-line"></i>
            Gestão de Projetos
          </Link>
        </div>
      )}
    </header>
  );
}
